<script setup>
import { ref, watch, computed } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import Checkbox from 'primevue/checkbox';
import { useRbacStore } from '@/stores/rbac.js';

const props = defineProps({
  visible: Boolean,
  mode: {
    type: String,
    default: 'create', // 'create' | 'edit'
    validator: (value) => ['create', 'edit'].includes(value)
  },
  position: {
    type: Object,
    default: null
  }
});

const emit = defineEmits(['update:visible', 'save', 'close']);

const rbacStore = useRbacStore();

const form = ref({
  name: '',
  department: null,
  check_ip: false
});

const isEdit = computed(() => props.mode === 'edit');
const dialogTitle = computed(() => isEdit.value ? 'Редактировать должность' : 'Создать должность');

// Список отделов для выбора
const departmentOptions = computed(() => {
  return rbacStore.departments.map(dept => ({
    label: dept.name,
    value: dept.id
  }));
});

watch(() => props.visible, async (newVal) => {
  if (newVal) {
    if (isEdit.value && props.position) {
      form.value = {
        name: props.position.name || '',
        department: props.position.department_id || null,
        check_ip: props.position.check_ip || false
      };
    } else {
      resetForm();
    }

    // Загружаем отделы если не загружены
    if (rbacStore.departments.length === 0) {
      await rbacStore.loadDepartments();
    }
  }
});

function resetForm() {
  form.value = {
    name: '',
    department: null,
    check_ip: false
  };
}

function handleSave() {
  if (!form.value.name.trim() || !form.value.department) {
    return;
  }

  const payload = {
    name: form.value.name.trim(),
    department: form.value.department,
    check_ip: form.value.check_ip
  };

  emit('save', payload);
}

function handleClose() {
  emit('update:visible', false);
  emit('close');
  resetForm();
}
</script>

<template>
  <Dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    :header="dialogTitle"
    modal
    :style="{ width: '600px' }"
  >
    <div class="flex flex-col gap-4">
      <!-- Название должности -->
      <div class="flex flex-col gap-2">
        <label for="pos-name" class="font-semibold">Название должности <span class="text-red-500">*</span></label>
        <InputText
          id="pos-name"
          v-model="form.name"
          placeholder="Введите название должности"
          class="w-full"
        />
      </div>

      <!-- Отдел -->
      <div class="flex flex-col gap-2">
        <label for="pos-dept" class="font-semibold">Отдел <span class="text-red-500">*</span></label>
        <Select
          id="pos-dept"
          v-model="form.department"
          :options="departmentOptions"
          option-label="label"
          option-value="value"
          placeholder="Выберите отдел"
          class="w-full"
          :filter="true"
        />
      </div>

      <!-- Проверка IP -->
      <div class="flex items-center gap-2">
        <Checkbox
          id="pos-check-ip"
          v-model="form.check_ip"
          :binary="true"
        />
        <label for="pos-check-ip" class="cursor-pointer">Проверять IP-адрес</label>
      </div>
    </div>

    <template #footer>
      <div class="flex justify-end gap-2">
        <Button label="Отмена" severity="secondary" @click="handleClose" rounded size="small" />
        <Button
          :label="isEdit ? 'Сохранить' : 'Создать'"
          severity="info"
          @click="handleSave"
          rounded
          size="small"
          :disabled="!form.name.trim() || !form.department"
        />
      </div>
    </template>
  </Dialog>
</template>

<style scoped>
</style>
